import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getPlayersByTeam, getPlayerFullName } from '../utils/playerUtils';
import { getTeamLogo, normalizeTeamName } from '../utils/teamUtils';

const Sidebar = ({ 
  players = [], 
  fixtures = [],
  onPlayerClick
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTeam, setSelectedTeam] = useState('Broncos');
  const [isMobile, setIsMobile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [round, setRound] = useState(getDefaultRound);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [matchups, setMatchups] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const currentYear = 2025;
  const [selectedMatchup, setSelectedMatchup] = useState(null);
  const [showHomeTeam, setShowHomeTeam] = useState(true);

  // Add getDefaultRound function
  function getDefaultRound() {
    if (!fixtures || fixtures.length === 0) return 'Round 1';
    
    const today = new Date();
    
    // Find the first fixture that hasn't been played yet
    const upcomingFixture = fixtures.find(fixture => {
      if (!fixture.Date) return false;
      
      const [datePart, timePart] = fixture.Date.split(' ');
      const [day, month, year] = datePart.split('/');
      const [hours, minutes] = timePart.split(':');
      
      const fixtureDate = new Date(year, month - 1, day, hours, minutes);
      return fixtureDate >= today;
    });
    
    if (upcomingFixture) {
      return `Round ${upcomingFixture['Round Number']}`;
    }
    
    // If no upcoming fixtures, get the last regular round
    const lastRegularRound = fixtures.reduce((lastRound, fixture) => {
      const roundNum = fixture['Round Number'];
      if (
        roundNum && 
        !isNaN(parseInt(roundNum)) && 
        !roundNum.includes('Finals') && 
        !roundNum.includes('Grand')
      ) {
        return Math.max(lastRound, parseInt(roundNum));
      }
      return lastRound;
    }, 1);
    
    return `Round ${lastRegularRound}`;
  }

  // Add getRounds function
  const getRounds = () => {
    if (!fixtures || fixtures.length === 0) return [];
    
    // Get unique round numbers from fixtures and format them
    return [...new Set(fixtures.map(f => f['Round Number']))]
      .filter(roundNum => !isNaN(parseInt(roundNum)))
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(roundNum => `Round ${roundNum}`);
  };

  // Update matchups when round changes
  useEffect(() => {
    const getMatchups = () => {
      // Extract round number without the "Round " prefix
      const currentRoundNumber = round.replace('Round ', '');
      
      // Filter fixtures directly by the round number from fixtures
      const roundFixtures = fixtures.filter(fixture => 
        fixture['Round Number'] === currentRoundNumber
      );
      
      const matchupsList = roundFixtures.map(fixture => ({
        homeTeam: fixture['Home Team'],
        awayTeam: fixture['Away Team'],
        date: fixture['Date'],
        location: fixture['Location']
      }));
      
      setMatchups(matchupsList);
      
      // Auto-select first matchup when matchups are loaded
      if (matchupsList.length > 0) {
        const firstMatchup = matchupsList[0];
        setSelectedMatchup({
          homeTeam: firstMatchup.homeTeam,
          awayTeam: firstMatchup.awayTeam
        });
        setShowHomeTeam(true); // Reset to show home team
      }
    };
    
    if (fixtures && fixtures.length > 0) {
      getMatchups();
    }
  }, [fixtures, round]);

  // Add click outside handler for round dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.round-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get unique teams from current players
  const teams = [...new Set(players
    .filter(p => p.year === currentYear)
    .map(p => {
      if (!p.player_team) {
        console.warn('Player missing player_team:', p);
        return null;
      }
      return normalizeTeamName(p.player_team);
    })
  )].filter(Boolean).sort();

  // Update getCurrentTeamPlayers to use selectedTeams
  const getCurrentTeamPlayers = (allPlayers, team) => {
    const currentPlayersMap = new Map();
    
    // Filter players for the current year and selected team
    allPlayers
      .filter(p => {
        const isCurrentYear = p.year === currentYear;
        const isSelectedTeam = normalizeTeamName(p.player_team) === team;
        // Include player if their team is selected in matchups
        const isInMatchup = selectedTeams.length === 0 || selectedTeams.includes(p.player_team);
        return isCurrentYear && isSelectedTeam && isInMatchup;
      })
      .forEach(player => {
        if (!currentPlayersMap.has(player.player_name)) {
          currentPlayersMap.set(player.player_name, player);
        }
      });
    
    return Array.from(currentPlayersMap.values())
      .sort((a, b) => a.player_name.localeCompare(b.player_name));
  };

  const teamPlayers = getCurrentTeamPlayers(players, selectedTeam);

  // Add media query check for mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // Standard mobile breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div 
      className={`h-full flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out ${
        !isMobile && isCollapsed ? 'w-16' : 'w-64'
      }`}
      onMouseEnter={() => !isMobile && setIsCollapsed(false)}
      onMouseLeave={() => !isMobile && setIsCollapsed(true)}
    >
      {/* Collapsed state view */}
      {!isMobile && isCollapsed && (
        <div className="flex flex-col items-center py-2 border-b border-gray-200 dark:border-gray-700">
          {selectedMatchup && (
            <>
              <img 
                src={getTeamLogo(selectedMatchup.homeTeam)} 
                alt={selectedMatchup.homeTeam}
                className="w-6 h-6 object-contain"
                onError={(e) => { e.target.style.display = 'none' }}
              />
              <div className="text-xs text-gray-400 dark:text-gray-500 my-0.5">vs</div>
              <img 
                src={getTeamLogo(selectedMatchup.awayTeam)} 
                alt={selectedMatchup.awayTeam}
                className="w-6 h-6 object-contain"
                onError={(e) => { e.target.style.display = 'none' }}
              />
            </>
          )}
        </div>
      )}

      {/* Main content */}
      <div className={`flex-1 flex flex-col overflow-hidden ${
        !isMobile && isCollapsed ? 'opacity-0' : 'opacity-100'
      } transition-opacity duration-300`}>
        {/* Round and Matchup Selection Section */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          {/* Round Selection with sideways navigation */}
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center justify-between gap-2">
              <button 
                onClick={() => {
                  const currentIndex = getRounds().indexOf(round);
                  if (currentIndex > 0) {
                    setRound(getRounds()[currentIndex - 1]);
                  }
                }}
                disabled={getRounds().indexOf(round) === 0}
                className={`p-1.5 rounded-md ${
                  getRounds().indexOf(round) === 0 
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' 
                    : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>

              <div className="flex-1 text-center">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {round === "OR" ? "Opening Round" : `${round}`}
                </span>
              </div>

              <button 
                onClick={() => {
                  const currentIndex = getRounds().indexOf(round);
                  if (currentIndex < getRounds().length - 1) {
                    setRound(getRounds()[currentIndex + 1]);
                  }
                }}
                disabled={getRounds().indexOf(round) === getRounds().length - 1}
                className={`p-1.5 rounded-md ${
                  getRounds().indexOf(round) === getRounds().length - 1
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          {/* Matchup Selector */}
          <select
            className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md mb-2"
            value={selectedMatchup ? `${selectedMatchup.homeTeam}-${selectedMatchup.awayTeam}` : ''}
            onChange={(e) => {
              if (e.target.value === '') {
                setSelectedMatchup(null);
              } else {
                const [homeTeam, awayTeam] = e.target.value.split('-');
                setSelectedMatchup({ homeTeam, awayTeam });
              }
            }}
          >
            <option value="">Select Matchup</option>
            {matchups.map((matchup, index) => (
              <option key={index} value={`${matchup.homeTeam}-${matchup.awayTeam}`}>
                {normalizeTeamName(matchup.homeTeam)} vs {normalizeTeamName(matchup.awayTeam)}
              </option>
            ))}
          </select>

          {/* Date and Location Display */}
          {selectedMatchup && fixtures.find(f => 
            f['Home Team'] === selectedMatchup.homeTeam && 
            f['Away Team'] === selectedMatchup.awayTeam
          ) && (
            <div className="mb-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">
                    {fixtures.find(f => 
                      f['Home Team'] === selectedMatchup.homeTeam && 
                      f['Away Team'] === selectedMatchup.awayTeam
                    )?.Date || 'Date TBD'}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-medium">
                    {fixtures.find(f => 
                      f['Home Team'] === selectedMatchup.homeTeam && 
                      f['Away Team'] === selectedMatchup.awayTeam
                    )?.Location || 'Location TBD'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Team Toggle (only show if matchup is selected) */}
          {selectedMatchup && (
            <div className="flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <button
                className={`flex-1 px-3 py-2 text-xs flex items-center justify-center gap-2 ${
                  showHomeTeam 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setShowHomeTeam(true)}
              >
                <img src={getTeamLogo(selectedMatchup.homeTeam)} alt="" className="h-4 w-4" />
                {normalizeTeamName(selectedMatchup.homeTeam)}
              </button>
              <button
                className={`flex-1 px-3 py-2 text-xs flex items-center justify-center gap-2 ${
                  !showHomeTeam 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setShowHomeTeam(false)}
              >
                <img src={getTeamLogo(selectedMatchup.awayTeam)} alt="" className="h-4 w-4" />
                {normalizeTeamName(selectedMatchup.awayTeam)}
              </button>
            </div>
          )}
        </div>

        {/* Player List */}
        <div className="flex-1 overflow-y-auto">
          {selectedMatchup ? (
            // Show players from selected team in matchup
            getCurrentTeamPlayers(
              players, 
              showHomeTeam ? selectedMatchup.homeTeam : selectedMatchup.awayTeam
            ).map(player => (
              <Link
                key={player.player_name}
                to={`/player/${encodeURIComponent(player.player_name)}`}
                className="block px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 last:border-0"
                onClick={onPlayerClick}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {player.player_name}
                </div>
              </Link>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              Select a matchup to view players
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;